/**
 * ===================================================
 * Module: frontend.src.components.RedactionListPanel.tsx
 * Description: Hierarchical checkbox list of redacted items
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * ===================================================
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RedactionBox } from '../types';
import '../styles/RedactionListPanel.css';

// Category display config (money is excluded from this release — issue #22)
const CATEGORY_CONFIG: Record<string, { order: number }> = {
  personal_name: { order: 1 },
  email: { order: 2 },
  tel: { order: 3 },
  address: { order: 4 },
  dob: { order: 5 },
  seal: { order: 7 },
  face: { order: 8 },
  number_plate: { order: 9 },
  other: { order: 10 },
  unknown: { order: 99 },
};

interface CategoryGroup {
  category: string;
  items: Array<{ boxId: string; box: RedactionBox }>;
}

// For text items: group by value, then show occurrences
interface TextValueGroup {
  text: string;
  items: Array<{ boxId: string; box: RedactionBox }>;
}

interface RedactionListPanelProps {
  redactionBoxes: Record<string, RedactionBox>;
  selectedBoxIds: Set<string>;
  // Checked (pinned) boxes — owned by PDFEditor so clicks on the PDF can
  // preserve them; the checkbox is the only way to unpin.
  checkedBoxIds: Set<string>;
  // Last box the user clicked — target of the properties editor
  focusedBoxId?: string | null;
  currentPage: number;
  onBoxSelect: (boxId: string, shiftKey: boolean) => void;
  onBoxDelete: (boxId: string) => void;
  onBoxUpdate: (boxId: string, updates: Partial<RedactionBox>) => void;
  onNavigateToBox: (boxId: string, page: number) => void;
  onBulkDelete: (boxIds: string[]) => void;
  onCheckedChange: (boxIds: string[], checked: boolean) => void;
  onExportList?: () => void;
}

const RedactionListPanel: React.FC<RedactionListPanelProps> = ({
  redactionBoxes,
  selectedBoxIds,
  checkedBoxIds,
  focusedBoxId,
  currentPage,
  onBoxSelect,
  onBoxDelete,
  onBoxUpdate,
  onNavigateToBox,
  onBulkDelete,
  onCheckedChange,
  onExportList,
}) => {
  const { t } = useTranslation('pdfEditor');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  const [editingBoxId, setEditingBoxId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState<string>('');

  // Per-page filter (issue #23) — when on, only items on the visible page are listed
  const [showCurrentPageOnly, setShowCurrentPageOnly] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);

  // Group boxes by category
  const categoryGroups = useMemo(() => {
    const groups: Record<string, CategoryGroup> = {};

    Object.entries(redactionBoxes).forEach(([boxId, box]) => {
      if (showCurrentPageOnly && box.page !== currentPage) return;
      const category = box.category || 'unknown';
      if (!groups[category]) {
        groups[category] = { category, items: [] };
      }
      groups[category].items.push({ boxId, box });
    });

    // Sort items within each group by page, then y position
    Object.values(groups).forEach(group => {
      group.items.sort((a, b) => {
        if (a.box.page !== b.box.page) return a.box.page - b.box.page;
        return a.box.bbox[1] - b.box.bbox[1];
      });
    });

    // Sort groups by configured order
    return Object.values(groups).sort((a, b) => {
      const orderA = CATEGORY_CONFIG[a.category]?.order ?? 99;
      const orderB = CATEGORY_CONFIG[b.category]?.order ?? 99;
      return orderA - orderB;
    });
  }, [redactionBoxes, showCurrentPageOnly, currentPage]);

  // For text categories, further group by text value (only group when text is meaningful)
  const getTextValueGroups = useCallback((items: Array<{ boxId: string; box: RedactionBox }>): TextValueGroup[] => {
    const groups: Record<string, TextValueGroup> = {};
    let unnamedIdx = 0;
    items.forEach(item => {
      const text = item.box.text && item.box.text !== '-' ? item.box.text : '';
      if (text) {
        // Group by actual text value
        if (!groups[text]) {
          groups[text] = { text, items: [] };
        }
        groups[text].items.push(item);
      } else {
        // No text — each item gets its own entry with sequential number
        unnamedIdx++;
        const key = `__unnamed_${unnamedIdx}`;
        groups[key] = { text: `#${unnamedIdx}`, items: [item] };
      }
    });
    return Object.values(groups);
  }, []);

  const isTextCategory = (category: string) => {
    return ['personal_name', 'email', 'tel', 'address', 'dob', 'other'].includes(category);
  };

  const toggleCategoryCollapse = (category: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Checkbox handling — checked state lives in PDFEditor and pins the box's
  // selection on the PDF (issue #11)
  const toggleCheckbox = (boxId: string) => {
    onCheckedChange([boxId], !checkedBoxIds.has(boxId));
  };

  const toggleCategoryCheckbox = (group: CategoryGroup) => {
    const allBoxIds = group.items.map(i => i.boxId);
    const allChecked = allBoxIds.every(id => checkedBoxIds.has(id));
    onCheckedChange(allBoxIds, !allChecked);
  };

  const toggleTextGroupCheckbox = (items: Array<{ boxId: string }>) => {
    const allBoxIds = items.map(i => i.boxId);
    const allChecked = allBoxIds.every(id => checkedBoxIds.has(id));
    onCheckedChange(allBoxIds, !allChecked);
  };

  const handleDeleteChecked = () => {
    if (checkedBoxIds.size > 0) {
      // PDFEditor confirms, deletes, and purges the checkbox state itself —
      // cancelling leaves boxes and checkboxes untouched.
      onBulkDelete(Array.from(checkedBoxIds));
    }
  };

  const handleBulkCategoryChange = (group: CategoryGroup, newCategory: string) => {
    // If specific items are checked, only move checked items; otherwise move all items in group (Issue #6 & #16)
    const checkedInGroup = group.items.filter(({ boxId }) => checkedBoxIds.has(boxId));
    const targetItems = checkedInGroup.length > 0 ? checkedInGroup : group.items;

    targetItems.forEach(({ boxId }) => {
      onBoxUpdate(boxId, { category: newCategory });
    });
  };

  const handleStartInlineEdit = (boxId: string, currentText: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingBoxId(boxId);
    setEditingText(currentText);
  };

  const handleSaveInlineEdit = (boxId: string) => {
    if (editingBoxId === boxId) {
      onBoxUpdate(boxId, { text: editingText });
      setEditingBoxId(null);
    }
  };

  const getCategoryLabel = (category: string): string => {
    const key = `redactionList.category.${category}`;
    const translated = t(key, { defaultValue: '' });
    if (translated && translated !== key) return translated;
    // Fallback
    return category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const totalItems = Object.keys(redactionBoxes).length;
  const visibleItems = categoryGroups.reduce((sum, g) => sum + g.items.length, 0);

  // Ordered list of category keys for the dropdown (money excluded — issue #22)
  const CATEGORY_OPTIONS = [
    'personal_name', 'email', 'tel', 'address', 'dob',
    'seal', 'face', 'number_plate', 'other',
  ];

  // The properties editor targets the last-clicked (focused) box; with pinned
  // checkboxes the selection is often plural, so size alone can't decide.
  const singleSelectedId = focusedBoxId ?? (selectedBoxIds.size === 1 ? Array.from(selectedBoxIds)[0] : null);
  const singleSelectedBox = singleSelectedId ? redactionBoxes[singleSelectedId] : null;

  // When a box is selected on the PDF, make its list item visible:
  // expand the category if collapsed and scroll the item into view (issue #20)
  useEffect(() => {
    if (!singleSelectedId || !singleSelectedBox) return;
    const category = singleSelectedBox.category || 'unknown';
    setCollapsedCategories(prev => {
      if (!prev.has(category)) return prev;
      const next = new Set(prev);
      next.delete(category);
      return next;
    });
    const raf = requestAnimationFrame(() => {
      const el = listRef.current?.querySelector(`[data-box-id="${CSS.escape(singleSelectedId)}"]`);
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [singleSelectedId, singleSelectedBox]);

  if (totalItems === 0) {
    return (
      <div className="redaction-list-panel">
        <div className="rlp-header">
          <h3 className="rlp-title">{t('redactionList.title', { defaultValue: '墨消し一覧' })}</h3>
        </div>
        <div className="rlp-empty">
          {t('redactionList.empty', { defaultValue: '検出項目はありません' })}
        </div>
      </div>
    );
  }

  return (
    <div className="redaction-list-panel">
      <div className="rlp-header">
        <h3 className="rlp-title">
          {t('redactionList.title', { defaultValue: '墨消し一覧' })}
          <span className="rlp-count">{totalItems}</span>
        </h3>
        <div className="rlp-header-actions">
          {checkedBoxIds.size > 0 && (
            <button className="rlp-delete-btn" onClick={handleDeleteChecked}>
              {t('redactionList.deleteSelected', { defaultValue: '削除', count: checkedBoxIds.size })}
              <span className="rlp-delete-count">{checkedBoxIds.size}</span>
            </button>
          )}
          {onExportList && (
            <button
              className="rlp-export-btn"
              onClick={onExportList}
              title={t('tooltips.exportList', { defaultValue: '墨消し一覧をCSVで出力' })}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              {t('redactionList.exportList', { defaultValue: '一覧を出力' })}
            </button>
          )}
        </div>
      </div>

      {/* Per-page filter (issue #23) */}
      <div className="rlp-filter-bar">
        <label className="rlp-filter-label">
          <input
            type="checkbox"
            className="rlp-checkbox"
            checked={showCurrentPageOnly}
            onChange={(e) => setShowCurrentPageOnly(e.target.checked)}
          />
          <span>{t('redactionList.currentPageOnly', { defaultValue: '現在表示中のページのみ表示' })}</span>
          {showCurrentPageOnly && <span className="rlp-filter-page">P{currentPage}</span>}
        </label>
      </div>

      {/* Properties editor — always visible. It previously mounted only while
          exactly one box was selected, so it flickered in and out as the
          selection changed (KTS: "property visibility timing"). */}
      <div className="rlp-properties">
        <div className="rlp-properties-header">
          <span className="rlp-properties-title">
            {t('redactionList.properties', { defaultValue: 'プロパティ' })}
          </span>
          {singleSelectedBox ? (
            <span className="rlp-properties-page">P{singleSelectedBox.page}</span>
          ) : (
            <span className="rlp-properties-hint">
              {selectedBoxIds.size > 1
                ? t('redactionList.multiSelected', { count: selectedBoxIds.size, defaultValue: `${selectedBoxIds.size}件選択中` })
                : t('redactionList.noSelection', { defaultValue: '未選択' })}
            </span>
          )}
        </div>
        <div className="rlp-properties-body">
          <div className="rlp-field-row">
            <div className="rlp-field">
              <label className="rlp-field-label">
                {t('redactionList.categoryLabel', { defaultValue: 'カテゴリ' })}
              </label>
              <select
                className="rlp-field-select"
                disabled={!singleSelectedId}
                value={singleSelectedBox?.category || ''}
                onChange={(e) => {
                  if (!singleSelectedId) return;
                  const val = e.target.value;
                  onBoxUpdate(singleSelectedId, { category: val || undefined });
                }}
              >
                <option value="">
                  {t('redactionList.selectCategory', { defaultValue: 'カテゴリを選択' })}
                </option>
                {CATEGORY_OPTIONS.map(cat => (
                  <option key={cat} value={cat}>
                    {getCategoryLabel(cat)}
                  </option>
                ))}
              </select>
            </div>
            {singleSelectedId && singleSelectedBox?.category && (
              <button
                className="rlp-clear-btn"
                onClick={() => onBoxUpdate(singleSelectedId, { category: undefined })}
                title={t('redactionList.clear', { defaultValue: 'クリア' })}
              >
                {t('redactionList.clear', { defaultValue: 'クリア' })}
              </button>
            )}
          </div>

          <div className="rlp-field">
            <label className="rlp-field-label">
              {t('redactionList.textLabel', { defaultValue: '内容' })}
            </label>
            <input
              type="text"
              className="rlp-field-input"
              disabled={!singleSelectedId}
              value={singleSelectedBox?.text || ''}
              placeholder={singleSelectedId
                ? t('redactionList.textPlaceholder', { defaultValue: '内容を入力' })
                : t('redactionList.selectPrompt', { defaultValue: '項目を選択してください' })}
              onChange={(e) => {
                if (!singleSelectedId) return;
                onBoxUpdate(singleSelectedId, { text: e.target.value || undefined });
              }}
            />
          </div>
        </div>
      </div>

      <div className="rlp-list" ref={listRef}>
        {visibleItems === 0 && showCurrentPageOnly && (
          <div className="rlp-empty">
            {t('redactionList.emptyPage', { defaultValue: 'このページに項目はありません' })}
          </div>
        )}
        {categoryGroups.map((group) => {
          const isCollapsed = collapsedCategories.has(group.category);
          const allBoxIds = group.items.map(i => i.boxId);
          const allChecked = allBoxIds.every(id => checkedBoxIds.has(id));
          const someChecked = allBoxIds.some(id => checkedBoxIds.has(id));

          return (
            <div key={group.category} className="rlp-category">
              <div className={`rlp-category-header ${!isCollapsed ? 'expanded' : ''}`}>
                <input
                  type="checkbox"
                  className="rlp-checkbox"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = someChecked && !allChecked;
                  }}
                  onChange={() => toggleCategoryCheckbox(group)}
                />
                <button
                  className="rlp-category-toggle"
                  onClick={() => toggleCategoryCollapse(group.category)}
                >
                  <span className="rlp-category-name">{getCategoryLabel(group.category)}</span>
                  <span className="rlp-category-count">{group.items.length}</span>
                  <svg
                    className={`rlp-chevron ${isCollapsed ? '' : 'open'}`}
                    width="14" height="14" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
                <select
                  className="rlp-category-reassign"
                  value=""
                  onChange={(e) => {
                    e.stopPropagation();
                    const newCategory = e.target.value;
                    if (newCategory) {
                      handleBulkCategoryChange(group, newCategory);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  title={t('redactionList.reassignCategory', { defaultValue: 'カテゴリを一括変更' })}
                >
                  <option value="" disabled>
                    {t('redactionList.moveTo', { defaultValue: '移動' })}
                  </option>
                  {CATEGORY_OPTIONS.filter(cat => cat !== group.category).map(cat => (
                    <option key={cat} value={cat}>
                      {getCategoryLabel(cat)}
                    </option>
                  ))}
                </select>
              </div>

              {!isCollapsed && (
                <div className="rlp-category-items">
                  {isTextCategory(group.category) ? (
                    // Text items: group by value
                    getTextValueGroups(group.items).map((textGroup, tIdx) => (
                      <div key={`${group.category}-${tIdx}`} className="rlp-text-group">
                        {textGroup.items.length > 1 ? (
                          // Multiple occurrences of same text
                          <>
                            <div
                              className={`rlp-text-value-header ${textGroup.items.some(i => selectedBoxIds.has(i.boxId)) ? 'selected' : ''}`}
                              onClick={() => onNavigateToBox(textGroup.items[0].boxId, textGroup.items[0].box.page)}
                            >
                              <input
                                type="checkbox"
                                className="rlp-checkbox"
                                checked={textGroup.items.every(i => checkedBoxIds.has(i.boxId))}
                                onChange={() => toggleTextGroupCheckbox(textGroup.items)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              {editingBoxId === textGroup.items[0].boxId ? (
                                <input
                                  type="text"
                                  className="rlp-inline-input"
                                  value={editingText}
                                  onChange={(e) => setEditingText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveInlineEdit(textGroup.items[0].boxId);
                                    if (e.key === 'Escape') setEditingBoxId(null);
                                  }}
                                  onBlur={() => handleSaveInlineEdit(textGroup.items[0].boxId)}
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span
                                  className="rlp-text-value"
                                  onDoubleClick={(e) => handleStartInlineEdit(textGroup.items[0].boxId, textGroup.text, e)}
                                  title="ダブルクリックで編集"
                                >
                                  {textGroup.text}
                                </span>
                              )}
                              <span className="rlp-occurrence-count">×{textGroup.items.length}</span>
                            </div>
                            <div className="rlp-occurrences">
                              {textGroup.items.map(({ boxId, box }) => (
                                <div
                                  key={boxId}
                                  data-box-id={boxId}
                                  className={`rlp-item rlp-occurrence ${selectedBoxIds.has(boxId) ? 'selected' : ''}`}
                                  onClick={() => onNavigateToBox(boxId, box.page)}
                                >
                                  <input
                                    type="checkbox"
                                    className="rlp-checkbox"
                                    checked={checkedBoxIds.has(boxId)}
                                    onChange={(e) => { e.stopPropagation(); toggleCheckbox(boxId); }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span className="rlp-page-badge">P{box.page}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          // Single occurrence
                          <div
                            data-box-id={textGroup.items[0].boxId}
                            className={`rlp-item ${selectedBoxIds.has(textGroup.items[0].boxId) ? 'selected' : ''}`}
                            onClick={() => onNavigateToBox(textGroup.items[0].boxId, textGroup.items[0].box.page)}
                          >
                            <input
                              type="checkbox"
                              className="rlp-checkbox"
                              checked={checkedBoxIds.has(textGroup.items[0].boxId)}
                              onChange={(e) => { e.stopPropagation(); toggleCheckbox(textGroup.items[0].boxId); }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            {editingBoxId === textGroup.items[0].boxId ? (
                              <input
                                type="text"
                                className="rlp-inline-input"
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveInlineEdit(textGroup.items[0].boxId);
                                  if (e.key === 'Escape') setEditingBoxId(null);
                                }}
                                onBlur={() => handleSaveInlineEdit(textGroup.items[0].boxId)}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <span
                                className="rlp-item-text"
                                onDoubleClick={(e) => handleStartInlineEdit(textGroup.items[0].boxId, textGroup.text, e)}
                                title="ダブルクリックで編集"
                              >
                                {textGroup.text}
                              </span>
                            )}
                            <span className="rlp-page-badge">P{textGroup.items[0].box.page}</span>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    // Non-text items (seal, face, license plate): show each detection
                    group.items.map(({ boxId, box }, idx) => (
                      <div
                        key={boxId}
                        data-box-id={boxId}
                        className={`rlp-item ${selectedBoxIds.has(boxId) ? 'selected' : ''}`}
                        onClick={() => onNavigateToBox(boxId, box.page)}
                      >
                        <input
                          type="checkbox"
                          className="rlp-checkbox"
                          checked={checkedBoxIds.has(boxId)}
                          onChange={(e) => { e.stopPropagation(); toggleCheckbox(boxId); }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="rlp-item-text">
                          {getCategoryLabel(group.category)} #{idx + 1}
                        </span>
                        <span className="rlp-page-badge">P{box.page}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RedactionListPanel;
